import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const ViewBooking = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userType = getUserType();
    const allowEdit = canEdit(userType, "bookings");
    const [id, setId] = useState(location.state?.booking?.id || "");
    const [bookingId, setBookingId] = useState(location.state?.booking?.booking_id || "");
    // Assuming unit_id and cust_id are available in the booking object or we need to fetch them
    // For now, initializing with values if they exist, otherwise will rely on fetched data
    const [unitId, setUnitId] = useState(location.state?.booking?.unit_id || "");
    const [cusId, setCusId] = useState(location.state?.booking?.customer_id || "");

    const [unitData, setUnitData] = useState([]);
    const [bookingData, setBookingData] = useState([]);
    const [stageData, setStageData] = useState([]);
    const [registryData, setRegistryData] = useState([]);
    const [chequeData, setChequeData] = useState([]);
    const [paymentData, setPaymentData] = useState([]);
    const [rePayData, setRePayData] = useState([]);
    const [additionData, setAdditionData] = useState([]);
    const [loanStatus, setLoanStatus] = useState([]);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    const [latestStage, setLatestStage] = useState(null);

    // Initial Fetch
    useEffect(() => {
        if (!id) {
            navigate("/dashboard/bookings");
            return;
        }
        fetchAllDetails();
    }, [id]);

    const fetchAllDetails = async () => {
        setLoading(true);
        try {
            await Promise.all([
                getUnitDetails(),
                getPaymentDetails(),
                getAdditionDetails(),
                getLoanDetails()
            ]);
            // Dependent fetches will be handled after unit details if needed, 
            // but RN code calls them all in useEffect. 
            // However, for getBookingDetails we need CusId, for getStageDetails we need unit_id.
            // If we don't have them in 'id' fetch, we might need to chain.
            // RN code assumes props.route.params has them.
            // let's try to fetch others if we have the IDs or if they don't depend on them
            // actually getStageDetails uses unit_id.
            // getRegistryDetails uses bookings_id.
            // getChequeDetails uses bookings_id.
            // getRePaymentDetails uses bookings_id.

            if (bookingId) {
                await Promise.all([
                    getRegistryDetails(),
                    getChequeDetails(),
                    getRePaymentDetails()
                ]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Dependent fetch effect
    useEffect(() => {
        if (unitData.length > 0) {
            // Check for customer_id and unit_id in unitData if not set
            const data = unitData[0];
            if (!cusId && data.customer_id) setCusId(data.customer_id);
            if (!unitId && data.unit_id) setUnitId(data.unit_id);
            // If the API returns them with different keys, adjust here.

            // RN code: let Unitname = unitdata.length > 0 ? unitdata[0].booking_id : '';
            if (!bookingId && data.booking_id) setBookingId(data.booking_id);
        }
    }, [unitData]);

    useEffect(() => {
        if (cusId) getBookingDetails();
    }, [cusId]);

    useEffect(() => {
        if (unitId) getStageDetails();
    }, [unitId]);

    useEffect(() => {
        if (bookingId) {
            getRegistryDetails();
            getChequeDetails();
            getRePaymentDetails();
        }
    }, [bookingId]);


    /* API Calls */
    const getUnitDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/anyrowsbyid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'bookings', id: id })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setUnitData(json.data);
        } catch (error) { console.error(error); }
    };

    const getBookingDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/allrowsbyid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'customers', id: cusId })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setBookingData(json.data);
        } catch (error) { console.error(error); }
    };

    const getStageDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/allstagebybid`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ table: 'construction_stage', id: unitId })
            });
            const json = await response.json();
            const stages = Array.isArray(json?.data) ? json.data : [];
            setStageData(stages);
            if (stages.length > 0) {
                const maxIdStage = stages.reduce((max, current) =>
                    Number(current.id) > Number(max.id) ? current : max
                );
                setLatestStage(maxIdStage);
            } else {
                setLatestStage(null);
            }
        } catch (error) { console.error(error); }
    };

    const getRegistryDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/allregistrybybid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'registry', id: bookingId })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setRegistryData(json.data);
        } catch (error) { console.error(error); }
    };

    const getChequeDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/allregistrybybid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'cheques', id: bookingId })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setChequeData(json.data);
        } catch (error) { console.error(error); }
    };

    const getPaymentDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/anyrowsbyid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'bookings', id: id })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setPaymentData(json.data);
        } catch (error) { console.error(error); }
    };

    const getRePaymentDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/analyser`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'payments', key: 'booking_id', id: bookingId })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setRePayData(json.data);
        } catch (error) { console.error(error); }
    };

    const getAdditionDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/alladdbybid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'additional_charges', id: id })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setAdditionData(json.data);
        } catch (error) { console.error(error); }
    };

    const getLoanDetails = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/allloanbybid`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: JSON.stringify({ table: 'loan_status', id: id })
            });
            const json = await response.json();
            if (Array.isArray(json.data)) setLoanStatus(json.data);
        } catch (error) { console.error(error); }
    };

    const sendDataToAPI = async () => {
        if (!cancelReason.trim()) {
            alert('All Fields Are Required');
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('id', id);
            formData.append('messages', cancelReason);

            const response = await fetch(`${API_BASE}/api/cancelquery`, {
                method: 'POST',
                headers: { 'Authorization': '1' },
                body: formData,
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                setModalVisible(false);
            }
        } catch (error) {
            alert('Something Went Wrong.');
        } finally {
            setLoading(false);
        }
    };


    /* Calculations */
    const formatAmount = (amount) => {
        if (!amount) return "₹0.00";
        return parseFloat(amount).toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    const stagepercent = latestStage?.percent;
    const totalCleared = chequeData.filter(item => item.c_status === 'Cleared').length;
    const totalReceived = chequeData.filter(item => item.c_status === 'Received').length;
    const totalPending = chequeData.filter(item => item.c_status === 'Pending').length;

    const totalAmount = paymentData.length > 0 ? paymentData.reduce((sum, p) => sum + (parseInt(p.sale_price, 10) || 0), 0) : 0;
    const loanamount = paymentData.length > 0 ? paymentData.reduce((sum, p) => sum + (parseInt(p.loan_amount, 10) || 0), 0) : 0;
    const GSTAMount = paymentData.length > 0 ? paymentData.reduce((sum, p) => sum + (parseInt(p.sale_gst, 10) || 0), 0) : 0;
    const totalPotAmount = paymentData.length > 0 ? paymentData.reduce((sum, p) => sum + (parseInt(p.land_cost, 10) || 0), 0) : 0;
    const constuction = paymentData.length > 0 ? paymentData.reduce((sum, p) => sum + (parseInt(p.construction_cost, 10) || 0), 0) : 0;

    const calculateTotal = (name) => additionData.filter(item => item.name === name).reduce((sum, item) => sum + (parseInt(item.cost, 10) || 0) + (parseInt(item.gst_cost, 10) || 0), 0);
    const calculateTotalCost = (name) => additionData.filter(item => item.name === name).reduce((sum, item) => sum + (parseInt(item.cost, 10) || 0), 0);

    const totalMainAmount = calculateTotal('Maintenence Charges');
    const totalClubAmount = calculateTotal('Club House Charges');
    const totalLoanAmount = calculateTotal('Loan Closure Return');
    const totalBuildingAmount = calculateTotal('Building Permission Charges');
    const totalRegistryAmount = calculateTotal('Registry Charges');

    const totalextraAmount = calculateTotalCost('Extra Work Charges');
    const ReferDiscount = calculateTotalCost('Refferral Discount');
    const totalDeductionAmount = calculateTotalCost('Deductions');
    const totalGstDiscount = calculateTotalCost('GST Discount');

    const totalRAmount = rePayData.length > 0 ? rePayData.reduce((sum, r) => sum + (parseInt(r.amount, 10) || 0), 0) : 0;
    const totalSelfAmount = rePayData.length > 0 ? rePayData.filter(r => r.payment_part === 'Self').reduce((sum, r) => sum + (parseInt(r.amount, 10) || 0), 0) : 0;
    const totalDueAAAmount = rePayData.length > 0 ? rePayData.filter(p => p.status === 'Confirmed' && p.payment_for !== 'Plot').reduce((sum, p) => sum + (parseInt(p.amount, 10) || 0), 0) : 0;

    const calculateRec = (paymentFor) => rePayData.length > 0 ? rePayData.filter(r => r.status === 'Confirmed' && r.payment_for === paymentFor).reduce((sum, r) => sum + (parseInt(r.amount, 10) || 0), 0) : 0;

    const totalPlotAmount = calculateRec('Plot');
    const totalBuildAmount = calculateRec('Building Permission Charge');
    const totalClubRec = calculateRec('Club House Charges');
    const totalMaintenRec = calculateRec('Maintenence Charges');
    const totalRegistryRec = calculateRec('Registry Charges');
    const totalLCloseR = calculateRec('Loan Closure Return');

    const totalBankAmount = rePayData.length > 0 ? rePayData.filter(r => r.payment_part === 'Bank Loan').reduce((sum, r) => sum + (parseInt(r.amount, 10) || 0), 0) : 0;

    const totalbankblc = loanamount - totalBankAmount;
    const totalDeductiongst = (totalDeductionAmount * 6) / 100;
    const extrGSTAMt = (totalextraAmount * 6) / 100;
    const gstValue = GSTAMount === 0 ? 0 : constuction * 0.06;
    const totalGSTUpper = gstValue + extrGSTAMt - totalDeductiongst;

    const totalSemiFinal = totalPotAmount + constuction + totalGSTUpper + totalextraAmount - (ReferDiscount + totalGstDiscount + totalDeductionAmount);

    const stagepayment = constuction + GSTAMount;
    const stagepayment1 = (stagepayment * stagepercent) / 100;
    const stagepayment2 = stagepayment1 + totalPotAmount;
    const stageDue = stagepayment1 - totalDueAAAmount;

    const GrandTotal2 = totalSemiFinal + totalLoanAmount + totalBuildingAmount + totalClubAmount + totalRegistryAmount + totalMainAmount;
    const grandtotalBalance = GrandTotal2 - totalRAmount;
    const totalDue = totalSemiFinal - totalRAmount;
    const totalPlotDue = totalPotAmount - totalPlotAmount;
    const staDue = stageDue + totalPlotDue;
    const realSelfAmt = totalSemiFinal - loanamount;
    const totalselfblc = realSelfAmt - totalSelfAmount;

    // The core data references for the PDF and WA message:
    const bd = unitData.length > 0 ? unitData[0] : {}; // booking details
    const cd = bookingData.length > 0 ? bookingData[0] : (unitData.length > 0 ? unitData[0] : {}); // customer details

    const generatePDFContent = () => {
        return `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Booking Details - ${bd.booking_id || 'N/A'}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Inter',sans-serif; color:#1e293b; background:#fff; padding:0; }
  .receipt { max-width:700px; margin:0 auto; padding:40px 36px; }

  /* Letterhead */
  .letterhead { text-align:center; border-bottom:3px solid #1e40af; padding-bottom:20px; margin-bottom:24px; }
  .company-name { font-size:26px; font-weight:800; color:#1e40af; text-transform:uppercase; letter-spacing:1.5px; }
  .company-sub { font-size:11px; color:#64748b; margin-top:4px; letter-spacing:.5px; font-weight:500; }
  .company-tag { font-size:10px; color:#94a3b8; margin-top:2px; }

  /* Receipt Header */
  .receipt-title-bar { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; padding:14px 18px; background:#f0f4ff; border-radius:10px; border:1px solid #dbeafe; }
  .receipt-title { font-size:18px; font-weight:700; color:#1e40af; }
  .receipt-id { font-size:13px; color:#475569; font-weight:600; }
  .receipt-date { font-size:12px; color:#64748b; margin-top:2px; }

  /* Detail Grid */
  .section { margin-bottom:22px; }
  .section-title { font-size:13px; font-weight:700; color:#1e40af; text-transform:uppercase; letter-spacing:.6px; margin-bottom:12px; padding-bottom:8px; border-bottom:1px solid #e2e8f0; }
  .detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:0; }
  .detail-row { display:flex; justify-content:space-between; padding:10px 14px; border-bottom:1px dashed #f1f5f9; }
  .detail-row:nth-child(odd) { background:#fafbff; }
  .detail-label { font-size:13px; color:#64748b; font-weight:500; }
  .detail-value { font-size:13px; color:#1e293b; font-weight:600; text-align:right; }

  /* Amount Highlight */
  .amount-box { background:linear-gradient(135deg,#1e40af,#3b82f6); color:#fff; padding:20px 24px; border-radius:14px; text-align:center; margin:20px 0; }
  .amount-label { font-size:12px; opacity:.85; font-weight:500; text-transform:uppercase; letter-spacing:.5px; }
  .amount-value { font-size:32px; font-weight:800; margin-top:4px; letter-spacing:-.5px; }

  /* Footer */
  .footer { border-top:2px solid #e2e8f0; padding-top:18px; margin-top:28px; display:flex; justify-content:space-between; align-items:flex-end; }
  .footer-left { font-size:11px; color:#94a3b8; line-height:1.7; }
  .footer-right { text-align:right; }
  .stamp-area { font-size:11px; color:#94a3b8; border-top:1px solid #cbd5e1; padding-top:8px; min-width:160px; text-align:center; }

  .watermark { position:fixed; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-30deg); font-size:80px; color:rgba(30,64,175,.04); font-weight:800; text-transform:uppercase; pointer-events:none; z-index:-1; }

  @media print {
    body { padding:0; }
    .receipt { padding:30px 24px; }
    @page { margin:15mm; size:A4; }
  }
</style>
</head>
<body>
<div class="watermark">BOOKING SUMMARY</div>
<div class="receipt">
  <!-- Letterhead -->
  <div class="letterhead">
    <div class="company-name">Sai Nath Buildcon Pvt. Ltd.</div>
    <div class="company-sub">Real Estate Development & Construction</div>
    <div class="company-tag">GSTIN: XXXXXXXXXXXX | CIN: XXXXXXXXXXXXX</div>
  </div>

  <!-- Receipt Title -->
  <div class="receipt-title-bar">
    <div>
      <div class="receipt-title">Booking Details</div>
      <div class="receipt-date">Unit: ${bd.unit || '—'} ${bd.unit2 ? '+ ' + bd.unit2 : ''}</div>
    </div>
    <div style="text-align:right">
      <div class="receipt-id">Booking ID: ${bd.booking_id || "—"}</div>
      <div class="receipt-date">Project: ${bd.project_name || "—"}</div>
    </div>
  </div>

  <!-- Customer Details -->
  <div class="section">
    <div class="section-title">Ownership Details</div>
    <div class="detail-row"><span class="detail-label">Customer Name</span><span class="detail-value">${cd.name || cd.customer_name || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Phone</span><span class="detail-value">${cd.phone || cd.cust_phone || "—"}</span></div>
    ${cd.gmail ? `<div class="detail-row"><span class="detail-label">Email</span><span class="detail-value">${cd.gmail}</span></div>` : ""}
  </div>

  <!-- Booking Details -->
  <div class="section">
    <div class="section-title">Booking Details</div>
    <div class="detail-row"><span class="detail-label">Plan Type</span><span class="detail-value">${bd.plan_type || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Sale Type</span><span class="detail-value">${bd.sale_type || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Build-Up Area</span><span class="detail-value">${bd.buildarea ? bd.buildarea + ' sq ft' : "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Agreement Type</span><span class="detail-value">${bd.agreement_type || "—"}</span></div>
    <div class="detail-row"><span class="detail-label">Source Of Payment</span><span class="detail-value">${bd.payment_source || "—"}</span></div>
  </div>

  <!-- Summary of Finances -->
  <div class="section">
    <div class="section-title">Sale Components</div>
    <div class="detail-row"><span class="detail-label">Actual Sale Price</span><span class="detail-value">${formatAmount(totalAmount)}</span></div>
    <div class="detail-row"><span class="detail-label">Land Part</span><span class="detail-value">${formatAmount(totalPotAmount)}</span></div>
    <div class="detail-row"><span class="detail-label">Const. Part</span><span class="detail-value">${formatAmount(constuction)}</span></div>
    <div class="detail-row"><span class="detail-label">Extra Work Amount</span><span class="detail-value">${formatAmount(totalextraAmount)}</span></div>
    <div class="detail-row"><span class="detail-label" style="color:#dc2626">Deductions</span><span class="detail-value" style="color:#dc2626">-${formatAmount(totalDeductionAmount)}</span></div>
    <div class="detail-row"><span class="detail-label" style="color:#dc2626">Referral Discount</span><span class="detail-value" style="color:#dc2626">-${formatAmount(ReferDiscount)}</span></div>
    <div class="detail-row" style="background:#f8fafc"><span class="detail-label" style="font-weight:700;color:#1e40af">Calculated Total</span><span class="detail-value" style="color:#1e40af">${formatAmount(totalSemiFinal)}</span></div>
  </div>

  <!-- Payments & Balances -->
  <div class="section">
    <div class="section-title">Payments & Ledgers</div>
    <div class="detail-row"><span class="detail-label">Received Amount</span><span class="detail-value">${formatAmount(totalRAmount)}</span></div>
    <div class="detail-row"><span class="detail-label">Overall Balance</span><span class="detail-value">${formatAmount(grandtotalBalance)}</span></div>
    <div class="detail-row"><span class="detail-label">Bank Balance</span><span class="detail-value">${formatAmount(totalbankblc)}</span></div>
    <div class="detail-row"><span class="detail-label">Self Balance</span><span class="detail-value">${formatAmount(totalselfblc)}</span></div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-left">
      This is a computer-generated booking summary.<br/>
      For any queries, contact Sai Nath Buildcon Pvt. Ltd.<br/>
      Generated on: ${new Date().toLocaleString("en-IN")}
    </div>
  </div>
</div>
</body>
</html>`;
    };

    const handleDownloadPDF = () => {
        const content = generatePDFContent();
        const printWindow = window.open("", "_blank");
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
            }, 500);
        };
    };

    const handleShareWhatsApp = () => {
        const text = `*BOOKING SUMMARY*
━━━━━━━━━━━━━
*Sai Nath Buildcon Pvt. Ltd.*
━━━━━━━━━━━━━

*Booking ID:* ${bd.booking_id || "—"}
*Project:* ${bd.project_name || "—"}
*Unit:* ${bd.unit || '—'} ${bd.unit2 ? '+ ' + bd.unit2 : ''}

*OWNERSHIP DETAILS*
Name: ${cd.name || cd.customer_name || "—"}
Phone: ${cd.phone || cd.cust_phone || "—"}
${cd.gmail ? `Email: ${cd.gmail}` : ""}

*BOOKING DETAILS*
Plan Type: ${bd.plan_type || "—"}
Sale Type: ${bd.sale_type || "—"}
Agreement: ${bd.agreement_type || "—"}
Payment Source: ${bd.payment_source || "—"}

*FINANCIAL SUMMARY*
Grand Total Amount: ${formatAmount(GrandTotal2)}
Total Received: ${formatAmount(totalRAmount)}
*Balance Due: ${formatAmount(grandtotalBalance)}*

*LEDGERS*
Bank Balance: ${formatAmount(totalbankblc)}
Self Balance: ${formatAmount(totalselfblc)}

━━━━━━━━━━━━━
_This is a system generated summary from Sai Nath Buildcon Pvt. Ltd._`;

        const phone = cd.phone || cd.cust_phone || "";
        const encodedText = encodeURIComponent(text.replace(/\n{3,}/g, "\n\n").trim());
        const url = phone
            ? `https://wa.me/${phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodedText}`
            : `https://wa.me/?text=${encodedText}`;
        window.open(url, "_blank");
    };

    return (
        <div className="vb-page">
            <style>
                {`
                .vb-page {
                    max-width: 1000px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                }
                .vb-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .vb-back-btn {
                    padding: 8px 16px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 500;
                    color: #334155;
                }
                .vb-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #0f172a;
                }
                .vb-add-btn {
                    color: #2563eb;
                    font-weight: 600;
                    cursor: pointer;
                    font-size: 14px;
                }
                .vb-actions { display:flex; gap:10px; flex-wrap:wrap; }
                .vb-btn { padding:8px 16px; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; border:none; display:flex; align-items:center; gap:6px; }
                .vb-btn:hover { transform:translateY(-1px); }
                .vb-btn-pdf { background:linear-gradient(135deg,#dc2626,#ef4444); color:#fff; box-shadow:0 2px 10px rgba(220,38,38,.2); }
                .vb-btn-pdf:hover { box-shadow:0 4px 16px rgba(220,38,38,.3); }
                .vb-btn-wa { background:linear-gradient(135deg,#16a34a,#22c55e); color:#fff; box-shadow:0 2px 10px rgba(22,163,74,.2); }
                .vb-btn-wa:hover { box-shadow:0 4px 16px rgba(22,163,74,.3); }
                .vb-btn-edit { background:#fff; color:#2563eb; border:1px solid #bfdbfe; }
                .vb-btn-edit:hover { background:#eff6ff; box-shadow:0 2px 10px rgba(37,99,235,.12); }
                @media(max-width:640px){
                  .vb-header { flex-direction:column; align-items:stretch; }
                  .vb-actions { justify-content:stretch; margin-top:10px; }
                  .vb-actions .vb-btn { flex:1; justify-content:center; }
                }
                .vb-card {
                    background: #fff;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                    margin-bottom: 20px;
                    border: 1px solid #e2e8f0;
                }
                .vb-section-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 16px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #f1f5f9;
                }
                .vb-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px dashed #f1f5f9;
                }
                .vb-row:last-child {
                    border-bottom: none;
                }
                .vb-label {
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 500;
                }
                .vb-value {
                    color: #1e293b;
                    font-size: 14px;
                    font-weight: 600;
                    text-align: right;
                }
                .vb-value-red {
                    color: #dc2626;
                    font-weight: 600;
                }
                .vb-loading {
                    display: flex;
                    justify-content: center;
                    padding: 40px;
                }
                .vb-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                .vb-modal {
                    background: #fff;
                    padding: 24px;
                    border-radius: 16px;
                    width: 90%;
                    max-width: 400px;
                }
                .vb-input {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    margin: 16px 0;
                }
                .vb-btn-red {
                    background: #ef4444;
                    color: #fff;
                    padding: 10px;
                    border-radius: 8px;
                    text-align: center;
                    cursor: pointer;
                    border: none;
                    width: 100%;
                    font-weight: 600;
                }
                `}
            </style>

            <div className="vb-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button className="vb-back-btn" onClick={() => navigate("/dashboard/bookings")}>
                        ← Back
                    </button>
                    <div className="vb-title">Booking Details</div>
                </div>
                <div className="vb-actions">
                    <button className="vb-btn vb-btn-pdf" onClick={handleDownloadPDF}>
                        📄 Download PDF
                    </button>
                    <button className="vb-btn vb-btn-wa" onClick={handleShareWhatsApp}>
                        💬 Share WhatsApp
                    </button>
                    {allowEdit && (
                        <button
                            className="vb-btn vb-btn-edit"
                            onClick={() => {
                                const bookingToEdit = unitData.length > 0 ? unitData[0] : location.state?.booking;
                                if (bookingToEdit) {
                                    navigate("/dashboard/bookings/edit", { state: { booking: bookingToEdit } });
                                }
                            }}
                        >
                            ✏️ Edit Booking
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="vb-loading">Loading details...</div>
            ) : (
                <>
                    {/* Ownership Details */}
                    <div className="vb-card">
                        <div className="vb-section-title">Ownership Details</div>
                        {bookingData.length > 0 ? bookingData.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="vb-row"><span className="vb-label">Name</span><span className="vb-value">{item.name || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Mobile</span><span className="vb-value">{item.phone || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Email</span><span className="vb-value">{item.gmail || 'N/A'}</span></div>
                            </React.Fragment>
                        )) : unitData.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="vb-row"><span className="vb-label">Customer Name</span><span className="vb-value">{item.customer_name || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Phone No</span><span className="vb-value">{item.cust_phone || 'N/A'}</span></div>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Booking Details */}
                    <div className="vb-card">
                        <div className="vb-section-title">Booking Details</div>
                        {unitData.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="vb-row"><span className="vb-label">Booking ID</span><span className="vb-value">{item.booking_id}</span></div>
                                <div className="vb-row"><span className="vb-label">Plan Type</span><span className="vb-value">{item.plan_type || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Sale Type</span><span className="vb-value">{item.sale_type || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Build-Up Area</span><span className="vb-value">{item.buildarea || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Unit No</span><span className="vb-value">{item.unit} {item.unit2 ? `+ ${item.unit2}` : ''}</span></div>
                                <div className="vb-row"><span className="vb-label">Project</span><span className="vb-value">{item.project_name || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Tally Entry</span><span className="vb-value">{item.tally_entry || 'N/A'}</span></div>
                                <div className="vb-row"><span className="vb-label">Update Status</span><span className="vb-value">{item.update_status || 'N/A'}</span></div>
                                {item.plan_type === 'Custom' && (
                                    <div className="vb-row"><span className="vb-label">Comment</span><span className="vb-value">{item.comment || 'N/A'}</span></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Sale Details */}
                    <div className="vb-card">
                        <div className="vb-section-title">Sale Details</div>
                        {unitData.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="vb-row"><span className="vb-label">Grand Total</span><span className="vb-value">{formatAmount(item.total_saleprice)}</span></div>
                                <div className="vb-row"><span className="vb-label">Actual Sale Price</span><span className="vb-value">{formatAmount(item.sale_price)}</span></div>
                                <div className="vb-row"><span className="vb-label">Land Part</span><span className="vb-value">{formatAmount(item.land_cost)}</span></div>
                                <div className="vb-row"><span className="vb-label">Construction Part</span><span className="vb-value">{formatAmount(item.construction_cost)}</span></div>
                                <div className="vb-row"><span className="vb-label">GST on Construction</span><span className="vb-value">{formatAmount(item.sale_gst)}</span></div>
                                <div className="vb-row"><span className="vb-label">Agreement Type</span><span className="vb-value">{item.agreement_type}</span></div>
                                <div className="vb-row"><span className="vb-label">Source Of Payment</span><span className="vb-value">{item.payment_source}</span></div>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Additional Charges */}
                    <div className="vb-card">
                        <div className="vb-section-title">Additional Charges</div>
                        {additionData.length > 0 ? additionData.map((item, i) => (
                            <div className="vb-row" key={i}>
                                <span className="vb-label">{item.name} {item.othermsg ? `(${item.othermsg})` : ''}</span>
                                <span className="vb-value">{formatAmount(item.cost)}</span>
                            </div>
                        )) : <div className="vb-row"><span className="vb-label">No data available</span></div>}
                    </div>

                    {/* GST */}
                    <div className="vb-card">
                        <div className="vb-section-title">GST</div>
                        {additionData.length > 0 ? additionData.map((item, i) => (
                            item.gst_cost !== '0' && (
                                <div className="vb-row" key={i}>
                                    <span className="vb-label">{item.name} {item.othermsg ? `(${item.othermsg})` : ''}</span>
                                    <span className="vb-value">{formatAmount(item.gst_cost)}</span>
                                </div>
                            )
                        )) : <div className="vb-row"><span className="vb-label">No data available</span></div>}
                    </div>

                    {/* Loan Details */}
                    {unitData.length > 0 && unitData[0].payment_source !== '100% Self' && (
                        <div className="vb-card">
                            <div className="vb-section-title">Loan Details</div>
                            <div className="vb-row">
                                <span className="vb-label">Loan Proposed</span>
                                <span className="vb-value">{formatAmount(loanamount)}</span>
                            </div>
                            {loanStatus.map((item, i) => (
                                <React.Fragment key={i}>
                                    <div className="vb-row">
                                        <span className="vb-label">{item.status} ({item.date})</span>
                                        <span className="vb-value">{formatAmount(item.loan_amount)}</span>
                                    </div>
                                    {item.status === 'Loan Applied' && (
                                        <div className="vb-row"><span className="vb-label">Manager Name</span><span className="vb-value">{item.loan_manager}</span></div>
                                    )}
                                    {item.status === 'Loan Sanctioned' && (
                                        <div className="vb-row"><span className="vb-label">Bank Name</span><span className="vb-value">{item.bank_name}</span></div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Registry Details */}
                    <div className="vb-card">
                        <div className="vb-section-title">Registry Details</div>
                        {registryData.length > 0 ? registryData.map((item, i) => (
                            <div key={i}>
                                <div className="vb-row"><span className="vb-label">Status</span><span className="vb-value">{item.status}</span></div>
                                {item.status === 'Open' && <div className="vb-row"><span className="vb-label">Date</span><span className="vb-value">{item.open_date}</span></div>}
                                {item.status === 'Scheduled' && <div className="vb-row"><span className="vb-label">Schedule Date</span><span className="vb-value">{item.schdule_date}</span></div>}
                                {item.status === 'Done' && <div className="vb-row"><span className="vb-label">Done Date</span><span className="vb-value">{item.done_date}</span></div>}
                            </div>
                        )) : <div className="vb-row"><span className="vb-label">No data available</span></div>}
                    </div>

                    {/* Construction Details */}
                    <div className="vb-card">
                        <div className="vb-section-title">Construction Details/Stages</div>
                        {unitData.map((item, i) => (
                            <div className="vb-row" key={i}><span className="vb-label">Construction Type</span><span className="vb-value">{item.construction_type}</span></div>
                        ))}
                        {Array.isArray(stageData) && stageData.map((item, i) => (
                            <div className="vb-row" key={i}>
                                <span className="vb-label">Stage Name: {item.stage_name}</span>
                                <span className="vb-value">{item.completion_date}</span>
                            </div>
                        ))}
                    </div>

                    {/* Payment Reports */}
                    <div className="vb-card">
                        <div className="vb-section-title">Payment Reports</div>
                        <div className="vb-row"><span className="vb-label">Actual Sale Value</span><span className="vb-value">{formatAmount(totalAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Actual Plot Value</span><span className="vb-value">{formatAmount(totalPotAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Actual Construction Value</span><span className="vb-value">{formatAmount(constuction)}</span></div>
                        <div className="vb-row"><span className="vb-label">Extra Work Amount</span><span className="vb-value">{formatAmount(totalextraAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Deductions</span><span className="vb-value vb-value-red">-{formatAmount(totalDeductionAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">GST Amount</span><span className="vb-value">{formatAmount(totalGSTUpper)}</span></div>
                        <div className="vb-row"><span className="vb-label">GST Discount</span><span className="vb-value vb-value-red">-{formatAmount(totalGstDiscount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Referral Discount</span><span className="vb-value vb-value-red">-{formatAmount(ReferDiscount)}</span></div>
                        <div className="vb-row" style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 8 }}><span className="vb-label" style={{ fontWeight: 700 }}>Total Amount</span><span className="vb-value">{formatAmount(totalSemiFinal)}</span></div>

                        <div className="vb-row"><span className="vb-label">Current Stage</span><span className="vb-value">{stagepercent || 0} %</span></div>
                        <div className="vb-row"><span className="vb-label">C.Stage Const. Amount</span><span className="vb-value">{formatAmount(stagepayment1)}</span></div>
                        <div className="vb-row"><span className="vb-label">Current Stage Due</span><span className="vb-value">{formatAmount(stagepayment2)}</span></div>
                        <div className="vb-row"><span className="vb-label">Actual Received Till Today</span><span className="vb-value">{formatAmount(totalRAmount)}</span></div>

                        <div className="vb-row"><span className="vb-label" style={{ fontWeight: 700 }}>Current Stage OverDue</span><span className="vb-value vb-value-red">{formatAmount(staDue)}</span></div>

                        <div className="vb-row"><span className="vb-label">Balance @ 100%</span><span className="vb-value">{formatAmount(totalDue)}</span></div>
                        <div className="vb-row"><span className="vb-label">Bank Balance</span><span className="vb-value">{formatAmount(totalbankblc)}</span></div>
                        <div className="vb-row"><span className="vb-label">Self Balance</span><span className="vb-value">{formatAmount(totalselfblc)}</span></div>

                        <div className="vb-row"><span className="vb-label">Maintainance Amount</span><span className="vb-value">{formatAmount(totalMainAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Maintainance Received</span><span className="vb-value">{formatAmount(totalMaintenRec)}</span></div>

                        <div className="vb-row"><span className="vb-label">Club House Charges</span><span className="vb-value">{formatAmount(totalClubAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Club House Received</span><span className="vb-value">{formatAmount(totalClubRec)}</span></div>

                        <div className="vb-row"><span className="vb-label">Building Permission Chrg</span><span className="vb-value">{formatAmount(totalBuildingAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Building Permission Received</span><span className="vb-value">{formatAmount(totalBuildAmount)}</span></div>

                        <div className="vb-row"><span className="vb-label">Loan Closure Return</span><span className="vb-value">{formatAmount(totalLoanAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Loan Closure Received</span><span className="vb-value">{formatAmount(totalLCloseR)}</span></div>

                        <div className="vb-row"><span className="vb-label">Registry Charges</span><span className="vb-value">{formatAmount(totalRegistryAmount)}</span></div>
                        <div className="vb-row"><span className="vb-label">Registry Charges Received</span><span className="vb-value">{formatAmount(totalRegistryRec)}</span></div>

                        <div style={{ marginTop: 16, background: '#f8fafc', padding: 12, borderRadius: 8 }}>
                            <div className="vb-row"><span className="vb-label">Grand Total Amount</span><span className="vb-value">{formatAmount(GrandTotal2)}</span></div>
                            <div className="vb-row"><span className="vb-label">Received Amount</span><span className="vb-value">{formatAmount(totalRAmount)}</span></div>
                            <div className="vb-row"><span className="vb-label">Grand Total Balance</span><span className="vb-value">{formatAmount(grandtotalBalance)}</span></div>
                        </div>

                        <div className="vb-row"><span className="vb-label">Cheque Received</span><span className="vb-value">{totalReceived}</span></div>
                        <div className="vb-row"><span className="vb-label">Cheque Cleared</span><span className="vb-value">{totalCleared}</span></div>
                        <div className="vb-row"><span className="vb-label">Cheque Pending</span><span className="vb-value">{totalPending}</span></div>
                    </div>

                    {allowEdit && (
                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <span className="vb-add-btn" style={{ color: '#dc2626' }} onClick={() => setModalVisible(true)}>Cancel Booking</span>
                        </div>
                    )}

                    {modalVisible && (
                        <div className="vb-modal-overlay">
                            <div className="vb-modal">
                                <h3>Enter Reason inside</h3>
                                <input
                                    className="vb-input"
                                    placeholder="Reason For Cancel"
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                />
                                <button className="vb-btn-red" onClick={sendDataToAPI}>Cancel Booking</button>
                                <button style={{ marginTop: 10, width: '100%', padding: 10, border: 'none', background: 'transparent', cursor: 'pointer' }} onClick={() => setModalVisible(false)}>Close</button>
                            </div>
                        </div>
                    )}

                </>
            )}
        </div>
    );
};

export default ViewBooking;
